import {Injectable, NgZone} from '@angular/core';
import * as moment from 'moment';
import {fromEvent, merge, Observable, of, Subject, Subscription, throwError} from 'rxjs';
import {SessionWarningDialogComponent} from '@core/dialog/session-warning-dialog/session-warning-dialog.component';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AuthService} from '@app/+auth/shared/services/auth.service';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {catchError, debounceTime, switchMap} from 'rxjs/operators';
import {Path} from '@core/enums';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  public sessionExpirationSubject = new Subject<boolean>();
  sessionExpiration$ = this.sessionExpirationSubject.asObservable();
  private sessionWarningTimer: any;
  private sessionExpirationTimer: any;

  private userActivitySubscription: Subscription | null = null;
  private dialogRefForSession: MatDialogRef<SessionWarningDialogComponent> | null = null;
  constructor(
    private authService: AuthService,
    private router: Router,
    private toasterService: ToastrService,
    private ngZone: NgZone,
    private dialog: MatDialog
  ) { }

  // Call this on application load
  initializeSessionTimers() {
    const tokenObj = this.getStoredToken();
    if (tokenObj?.expiresIn) {
      const expirationTime = tokenObj.expiresIn;
      const now = moment();

      if (moment(expirationTime).isAfter(now)) {
        this.startSessionTimers(expirationTime);
      } else {
        this.handleSessionExpiration();
      }
    } else {
      console.log('[SessionService] No expiration time found in localStorage.');
    }
  }

  startSessionTimers(expirationTime: number) {
    const now = moment(); // Capture the current time
    const expirationMoment = moment(expirationTime);
    const warningMoment = expirationMoment.clone().subtract(1, 'minutes'); // 1 minute before expiration

    // Calculate times
    const warningTime = warningMoment.diff(now);
    const expirationTimeMs = expirationMoment.diff(now);

    // Clear existing timers
    this.clearSessionTimers();

    // Set timer for warning
    if (warningTime > 0) {
      this.sessionWarningTimer = setTimeout(() => {
        this.sessionExpirationSubject.next(true); // Emit warning signal
      }, warningTime);
    } else {
      this.sessionExpirationSubject.next(true);
    }

    // Set timer for expiration
    if (expirationTimeMs > 0) {
      this.sessionExpirationTimer = setTimeout(() => {
        this.handleSessionExpiration();
      }, expirationTimeMs);
    } else {
      console.log('[SessionService] Expiration time is in the past. No timer set.');
    }
  }


  extendSession(isFromDialog: boolean) {
    console.log('In extend session');
    const tokenObj = this.getStoredToken();
    if (!tokenObj?.refreshToken) {
      console.error('No valid token available for refreshing the session.');
      this.handleSessionExpiration();
      return;
    }

    if (!isFromDialog) {
      const expiresAt = moment.utc(tokenObj.expiresIn);
      const now = moment.utc();
      const sessionDuration = expiresAt.diff(now, 'minutes');
      const startAutoRefresh = (sessionDuration / 2) + 1;

      if (sessionDuration > startAutoRefresh) {
        console.log(`[Session] Token is still valid, skipping refresh. Auto-refresh starts at ${startAutoRefresh} minutes`);
        return;
      }
    }

    this.refreshToken(tokenObj).pipe(
      switchMap((newTokenObj: any) => {
        this.storeToken(newTokenObj.body.tokenObj);
        this.startSessionTimers(newTokenObj.body.tokenObj.expiresIn);
        return of(true);
      }),
      catchError((error) => {
        console.error('[Session] Refresh token failed:', error);
        this.handleSessionExpiration();
        return throwError(() => error);
      })
    ).subscribe();
  }

  private refreshToken(tokenObj: any): Observable<any> {
    const userInformation = JSON.parse(localStorage.getItem('_user') || '{}');
    return this.authService.getRefreshToken({
      email: userInformation.email,
      userId: userInformation._id,
      refreshToken: tokenObj.refreshToken,
    });
  }


  handleSessionExpiration() {
    this.clearSessionTimers();
    this.sessionExpirationSubject.next(false); // Notify expiration
  }

  clearSessionTimers() {
    if (this.sessionWarningTimer) {
      clearTimeout(this.sessionWarningTimer);
    }
    if (this.sessionExpirationTimer) {
      clearTimeout(this.sessionExpirationTimer);
    }
  }

  async handleSessionExpired() {
    this.stopUserActivityTracking();
    console.log('[Session] Session expired, logging out user.');
    this.toasterService.info('Please log in again.', 'Session Expired');
    this.authService.logoutLocal();
    console.log('Logout Successful');
    this.router.navigate([`/${Path.App}/${Path.SignIn}`]);
  }

  openSessionWarningDialog() {
    console.log('dialog open came', this.dialogRefForSession);
    if (this.dialogRefForSession) {
      console.log('[SessionService] Warning dialog is already open.');
      return;
    }

    this.dialog.closeAll();  // Close any other open dialogs
    // this.stopUserActivityTracking();

    console.log('dialogRefForSession length', this.dialog.openDialogs.length );

    this.dialogRefForSession = this.dialog.open(SessionWarningDialogComponent, {
      width: '300px'
    });
    console.log('dialogRefForSession', this.dialogRefForSession);
    console.log('dialogRefForSession length', this.dialog.openDialogs.length );
    this.dialogRefForSession.afterClosed().subscribe((result) => {
      console.log('Dialog afterClosed event:', result);
      this.dialogRefForSession = null; // Reset dialog reference
      if (result === 'extend') {
        this.extendSession(true);
      } else {
        this.handleSessionExpired();
      }
    });
  }

  private getStoredToken() {
    return JSON.parse(localStorage.getItem('_token') || '{}');
  }

  private storeToken(tokenObj: any): void {
    localStorage.setItem('_token', JSON.stringify(tokenObj));
  }

  trackUserActivity() {
    console.log('[Session] Resuming user activity tracking');
    if (this.userActivitySubscription && !this.userActivitySubscription.closed) { return; }

    const tokenObj = this.getStoredToken();
    if (!tokenObj?.expiresIn) {
      console.log('[Session] No valid session, stopping tracking.');
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const userActivity$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click')
      ).pipe(debounceTime(2000));

      this.userActivitySubscription = userActivity$.subscribe(() => {
        console.log('Calling session extension from tracking');
        this.extendSession(false);
      });
    });
  }

  stopUserActivityTracking() {
    console.log('[Session] Stopping user activity tracking');
    if (this.userActivitySubscription) {
      this.userActivitySubscription.unsubscribe();
      this.userActivitySubscription = null;
    }
  }
}
